import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Skeleton, ErrorState } from '@/components/ui'
import { CreditCard, TrendingUp, Calendar, ExternalLink } from 'lucide-react'
import { api, type OrgBillingInfo } from '@/api/client'

export function OrgBillingPage() {
  const { orgSlug } = useParams()
  const [billing, setBilling] = useState<OrgBillingInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchBilling = async () => {
      if (!orgSlug) return
      try {
        setError(null)
        const data = await api.orgs.getBilling(orgSlug)
        setBilling(data)
      } catch (err) {
        console.error('Failed to fetch billing:', err)
        setError(err instanceof Error ? err.message : 'Failed to load billing')
      } finally {
        setIsLoading(false)
      }
    }
    fetchBilling()
  }, [orgSlug])

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Billing" description="Manage your subscription and payments" />
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="pt-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <Skeleton className="h-4 w-20 mb-2" />
                      <Skeleton className="h-8 w-24" />
                    </div>
                    <Skeleton className="h-11 w-11 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader><Skeleton className="h-5 w-28" /></CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Billing" description="Manage your subscription and payments" />
        <ErrorState title="Failed to load billing" description={error} action={<Button onClick={() => window.location.reload()}>Try Again</Button>} />
      </div>
    )
  }

  const stats = billing?.stats || { monthly_spend: 0, projected_spend: 0, active_apps: 0 }
  const plans = billing?.plans || []
  const invoices = billing?.invoices || []
  const paymentMethod = billing?.payment_method

  return (
    <div>
      <PageHeader title="Billing" description="Manage your subscription and payments" />
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-0">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">This Month</p>
                  <p className="text-3xl font-semibold text-[var(--text-primary)] mt-1">${stats.monthly_spend.toFixed(2)}</p>
                </div>
                <div className="rounded-full bg-[var(--bg-elevated-2)] p-3">
                  <CreditCard className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-0">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Projected</p>
                  <p className="text-3xl font-semibold text-[var(--text-primary)] mt-1">${stats.projected_spend.toFixed(2)}</p>
                </div>
                <div className="rounded-full bg-[var(--bg-elevated-2)] p-3">
                  <TrendingUp className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-0">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Next Invoice</p>
                  <p className="text-3xl font-semibold text-[var(--text-primary)] mt-1">{billing?.next_invoice_date || 'N/A'}</p>
                </div>
                <div className="rounded-full bg-[var(--bg-elevated-2)] p-3">
                  <Calendar className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Current Plan */}
        {plans.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Current Plan</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={`rounded-lg border p-4 ${plan.current ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border-default)]'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-[var(--text-primary)]">{plan.name}</h3>
                      {plan.current && <Badge variant="success">Current</Badge>}
                    </div>
                    <p className="text-2xl font-semibold text-[var(--text-primary)]">
                      {typeof plan.price === 'number' ? `$${plan.price}` : plan.price}
                      {typeof plan.price === 'number' && <span className="text-sm font-normal text-[var(--text-muted)]">/mo</span>}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-[var(--text-secondary)]">
                      <li>Up to {plan.users} users</li>
                      <li>Up to {plan.apps} apps</li>
                    </ul>
                    {!plan.current && (
                      <Button variant={plan.name === 'Enterprise' ? 'secondary' : 'default'} className="w-full mt-4">
                        {plan.name === 'Enterprise' ? 'Contact Sales' : 'Upgrade'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Method */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Payment Method</CardTitle>
              <Button variant="secondary" size="sm">Update</Button>
            </div>
          </CardHeader>
          <CardContent>
            {paymentMethod ? (
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)]">
                  <CreditCard className="h-6 w-6 text-[var(--text-secondary)]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{paymentMethod.brand} ending in {paymentMethod.last4}</p>
                  <p className="text-sm text-[var(--text-muted)]">Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)]">No payment method on file</p>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        {invoices.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Invoice History</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border-subtle)]">
                    <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">Date</th>
                    <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">Amount</th>
                    <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">Status</th>
                    <th className="text-right text-xs font-medium text-[var(--text-secondary)] pb-3">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-[var(--border-subtle)] last:border-0">
                      <td className="py-4 text-sm text-[var(--text-primary)]">{invoice.date}</td>
                      <td className="py-4 text-sm text-[var(--text-primary)]">${invoice.amount.toFixed(2)}</td>
                      <td className="py-4"><Badge variant={invoice.status === 'paid' ? 'success' : 'default'}>{invoice.status}</Badge></td>
                      <td className="py-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => invoice.url && window.open(invoice.url, '_blank')}>
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
